import React, { createContext, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useUser } from './user-provider';
import useApi from '../hooks/use-api';
import buildApiRequestConfig from '../api/url-builder';
import routes from '../api/routes';

export interface ColorSchemeColors {
    primaryColor: string;
    pageBackgroundColor: string;
    containerBackgroundColor: string;
    defaultTextColor: string;
    provisionsTextColor: string;
    transactionsTextColor: string;
    negativeAmountColor: string;
    endOfHorizonBackgroundColor: string;
    endOfHorizonTextColor: string;
    filterResultBackgroundColor: string;
    filterResultTextColor: string;
    runOutOfMoneyBackgroundColor: string;
    currentDayBackgroundColor: string;
    simulationRowBackgroundColor: string;
    transactionRowBackgroundColor: string;
    provisionRowBackgroundColor: string;
    cashflowIndicatorNegativeBackgroundColor: string;
    cashflowIndicatorNegativeTextColor: string;
    cashflowIndicatorPositiveBackgroundColor: string;
    cashflowIndicatorPositiveTextColor: string;
    // Per provision-type row colors (Transactions page) - override
    // provisionRowBackgroundColor/provisionsTextColor for whichever of these
    // categories a given unlinked provision row falls into.
    oneTimeProvisionBackgroundColor: string;
    oneTimeProvisionTextColor: string;
    recurringProvisionBackgroundColor: string;
    recurringProvisionTextColor: string;
    invoiceProvisionBackgroundColor: string;
    invoiceProvisionTextColor: string;
    payrollProvisionBackgroundColor: string;
    payrollProvisionTextColor: string;
}

// Matches the picker defaults in pages/color-scheme-settings.tsx - what every
// user sees until they save their own scheme.
export const DEFAULT_COLORS: ColorSchemeColors = {
    primaryColor: '#00B69B',
    pageBackgroundColor: '#FFFFFF',
    containerBackgroundColor: '#E0E0E0',
    defaultTextColor: '#000000',
    provisionsTextColor: '#FF00BF',
    transactionsTextColor: '#000000',
    negativeAmountColor: '#d32f2f',
    endOfHorizonBackgroundColor: '#ffa2a2',
    endOfHorizonTextColor: '#cc0000',
    filterResultBackgroundColor: '#ffda9d',
    filterResultTextColor: '#cc7700',
    runOutOfMoneyBackgroundColor: '#FFEBEE',
    currentDayBackgroundColor: '#E3F2FD',
    simulationRowBackgroundColor: '#FAFAFA',
    transactionRowBackgroundColor: '#DAE1F5',
    provisionRowBackgroundColor: '#FFFFFF',
    cashflowIndicatorNegativeBackgroundColor: '#d32f2f',
    cashflowIndicatorNegativeTextColor: '#A80000',
    cashflowIndicatorPositiveBackgroundColor: '#388E3C',
    cashflowIndicatorPositiveTextColor: '#0D6832',
    oneTimeProvisionBackgroundColor: '#FFFFFF',
    oneTimeProvisionTextColor: '#FF00BF',
    recurringProvisionBackgroundColor: '#E8F0FE',
    recurringProvisionTextColor: '#FF00BF',
    invoiceProvisionBackgroundColor: '#E6F7F4',
    invoiceProvisionTextColor: '#00897B',
    payrollProvisionBackgroundColor: '#FFF3E0',
    payrollProvisionTextColor: '#EF6C00',
};

// CSS custom property name for each color, applied to :root so any component
// can reference `var(--color-x)` directly in an sx/style prop without needing
// to consume the context itself.
const CSS_VAR_NAMES: Record<keyof ColorSchemeColors, string> = {
    primaryColor: '--color-primary',
    pageBackgroundColor: '--color-page-background',
    containerBackgroundColor: '--color-container-background',
    defaultTextColor: '--color-default-text',
    provisionsTextColor: '--color-provision-text',
    transactionsTextColor: '--color-transaction-text',
    negativeAmountColor: '--color-negative-amount',
    endOfHorizonBackgroundColor: '--color-end-of-horizon-bg',
    endOfHorizonTextColor: '--color-end-of-horizon-text',
    filterResultBackgroundColor: '--color-filter-result-bg',
    filterResultTextColor: '--color-filter-result-text',
    runOutOfMoneyBackgroundColor: '--color-run-out-of-money-bg',
    currentDayBackgroundColor: '--color-current-day-bg',
    simulationRowBackgroundColor: '--color-simulation-row-bg',
    transactionRowBackgroundColor: '--color-transaction-row-bg',
    provisionRowBackgroundColor: '--color-provision-row-bg',
    cashflowIndicatorNegativeBackgroundColor: '--color-cashflow-negative-bg',
    cashflowIndicatorNegativeTextColor: '--color-cashflow-negative-text',
    cashflowIndicatorPositiveBackgroundColor: '--color-cashflow-positive-bg',
    cashflowIndicatorPositiveTextColor: '--color-cashflow-positive-text',
    oneTimeProvisionBackgroundColor: '--color-onetime-provision-bg',
    oneTimeProvisionTextColor: '--color-onetime-provision-text',
    recurringProvisionBackgroundColor: '--color-recurring-provision-bg',
    recurringProvisionTextColor: '--color-recurring-provision-text',
    invoiceProvisionBackgroundColor: '--color-invoice-provision-bg',
    invoiceProvisionTextColor: '--color-invoice-provision-text',
    payrollProvisionBackgroundColor: '--color-payroll-provision-bg',
    payrollProvisionTextColor: '--color-payroll-provision-text',
};

// Parses '#RRGGBB' into "R, G, B" so callers needing an alpha-blended variant
// can use rgba(var(--color-primary-rgb), 0.1) - a plain CSS var can't carry
// its own alpha suffix the way a literal hex-with-alpha (#00B69B10) could.
const hexToRgbString = (hex: string): string => {
    const clean = hex.replace('#', '');
    const full = clean.length === 3 ? clean.split('').map(c => c + c).join('') : clean;
    const bigint = parseInt(full, 16);
    if (Number.isNaN(bigint)) return '0, 182, 155';
    return `${(bigint >> 16) & 255}, ${(bigint >> 8) & 255}, ${bigint & 255}`;
};

// Whether a hex color reads as "dark" - used to decide dark-appropriate
// assets (e.g. a light-on-dark logo variant) for whatever background color
// the user actually picked, not just when a preset literally named "Dark
// Mode" is applied.
export const isDarkColor = (hex: string): boolean => {
    const clean = hex.replace('#', '');
    const full = clean.length === 3 ? clean.split('').map(c => c + c).join('') : clean;
    const bigint = parseInt(full, 16);
    if (Number.isNaN(bigint)) return false;
    const r = (bigint >> 16) & 255, g = (bigint >> 8) & 255, b = bigint & 255;
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance < 0.5;
};

// Darkens a hex color by `factor` (0-1, fraction kept) - used to derive a
// hover shade for primaryColor without a dedicated picker field for it.
const darken = (hex: string, factor: number): string => {
    const clean = hex.replace('#', '');
    const full = clean.length === 3 ? clean.split('').map(c => c + c).join('') : clean;
    const bigint = parseInt(full, 16);
    if (Number.isNaN(bigint)) return hex;
    const r = Math.round(((bigint >> 16) & 255) * factor);
    const g = Math.round(((bigint >> 8) & 255) * factor);
    const b = Math.round((bigint & 255) * factor);
    const toHex = (v: number) => v.toString(16).padStart(2, '0');
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

// Every CSS custom property a `colors` object resolves to, including the
// derived ones (rgb triplets, hover/muted shades). Single source of truth so
// the live app (applied to :root) and any scoped preview (applied to a local
// wrapper's inline style) can never drift apart.
export const buildCssVars = (colors: ColorSchemeColors): Record<string, string> => {
    const vars: Record<string, string> = {};
    (Object.keys(CSS_VAR_NAMES) as (keyof ColorSchemeColors)[]).forEach((key) => {
        vars[CSS_VAR_NAMES[key]] = colors[key];
    });
    vars['--color-primary-rgb'] = hexToRgbString(colors.primaryColor);
    vars['--color-primary-hover'] = darken(colors.primaryColor, 0.85);
    vars['--color-default-text-rgb'] = hexToRgbString(colors.defaultTextColor);
    vars['--color-text-muted'] = 'rgba(var(--color-default-text-rgb), 0.6)';
    vars['--color-cashflow-negative-bg-rgb'] = hexToRgbString(colors.cashflowIndicatorNegativeBackgroundColor);
    vars['--color-cashflow-positive-bg-rgb'] = hexToRgbString(colors.cashflowIndicatorPositiveBackgroundColor);
    vars['--color-transaction-row-bg-rgb'] = hexToRgbString(colors.transactionRowBackgroundColor);
    vars['--color-provision-row-bg-rgb'] = hexToRgbString(colors.provisionRowBackgroundColor);
    return vars;
};

interface ColorSchemeContextType extends ColorSchemeColors {
    isLoading: boolean;
    isDark: boolean;
    reload: () => void;
}

const ColorSchemeContext = createContext<ColorSchemeContextType>({
    ...DEFAULT_COLORS,
    isLoading: false,
    isDark: false,
    reload: () => {},
});

export const ColorSchemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { user } = useUser();
    const [fetchResult, setFetchConfig] = useApi();
    const [colors, setColors] = useState<ColorSchemeColors>(DEFAULT_COLORS);
    const [isLoading, setIsLoading] = useState(true);
    // The admin portal keeps FineAnts' own look, whatever company's scheme
    // was last loaded (e.g. after an admin ends an impersonation).
    const isAdminRoute = useLocation().pathname.startsWith('/admin');
    const activeColors = isAdminRoute ? DEFAULT_COLORS : colors;

    const load = React.useCallback(() => {
        if (!user?.entityId) return;
        const { url, method } = buildApiRequestConfig(routes.colorScheme.fetch, [
            { param: 'entityId', value: user.entityId },
        ]);
        setFetchConfig({ url, method });
    }, [user?.entityId, setFetchConfig]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        const { data, isLoading: loading } = fetchResult;
        if (loading) return;
        if (data?.colors) {
            setColors({ ...DEFAULT_COLORS, ...data.colors });
        }
        setIsLoading(false);
    }, [fetchResult]);

    // Publish as CSS custom properties so plain sx/style color literals
    // (`var(--color-primary)` etc.) across the app stay in sync without every
    // component needing to consume this context directly.
    useEffect(() => {
        const root = document.documentElement;
        const vars = buildCssVars(activeColors);
        Object.entries(vars).forEach(([name, value]) => root.style.setProperty(name, value));
    }, [activeColors]);

    return (
        <ColorSchemeContext.Provider value={{ ...activeColors, isLoading, isDark: isDarkColor(activeColors.pageBackgroundColor), reload: load }}>
            {children}
        </ColorSchemeContext.Provider>
    );
};

export const useColorScheme = () => useContext(ColorSchemeContext);
